import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map } from 'rxjs/operators';
import { LoginService } from '../services/login.service';
import { ssoConnections } from '../state/sso.connections';
import {
  login,
  loginSuccess,
  loginFailure,
  loginCheckEmailDomain,
  loginToAzureADSuccess,
  loginShowPassword,
  loginVerify2FACode,
  loginVerify2FACodeFailure,
  recoveryVerifyCode
} from './login.actions';
import {
  authRedirectAfterLogin,
  authSetIdentityAndBootup,
  authSetIdentityWithoutBooting,
  authSetIdentityWithoutBootingAndGenerateSecret,
} from '@app/auth/core/state/auth.actions';
import { Identity } from '@app/auth/models';
import { MsalService } from '@app/msal/msal.service';
import { StorageKeys, StorageService } from '@app/core/services/storage/storage.service';

@Injectable()
export class LoginEffects {
  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(login),
      map(action => action.payload),
      exhaustMap(credentials => {
        return this.loginService.login$(credentials).pipe(
          concatMap((resp: Identity) => {
            if (resp.status === 'needs_2fa_code_verification') {
              return [authSetIdentityWithoutBooting(resp)];
            }

            if (resp.status === 'needs_2fa_setup') {
              return [authSetIdentityWithoutBootingAndGenerateSecret(resp)];
            }

            if (resp.status === 'authenticated') {
              return [loginSuccess(), authRedirectAfterLogin({ path: '' }), authSetIdentityAndBootup(resp)];
            }

            return [loginFailure({ payload: { error: 'status.invalid' } })];
          }),
          catchError((response: HttpErrorResponse) => {
            if (response.status === 401) {
              const { attemptsLeft: attempts = 0, banned = 0 } = response.error;

              return of(
                loginFailure({
                  payload: {
                    error: response.error.message || response.error,
                    attemptsLeft: Number(attempts),
                    bannedTime: Number(banned),
                  },
                }),
              );
            }

            return of(loginFailure({
              payload: {
                error: response.error.message || response.error
              }
            }));
          }),
        );
      }),
    );
  });

  checkEmailDomain$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loginCheckEmailDomain),
      map(action => action.payload),
      concatMap(email => {
        this.storage.set(StorageKeys.MSAL_EMAIL, email);
        const connection = ssoConnections.getConnection(email);
        if (connection) {
          this.msalService.clearMSALHistory();

          return this.msalService.loginRedirect$().pipe(
            map(() => {
              return loginToAzureADSuccess();
            }),
          );
        }

        return [loginShowPassword()];
      }),
    );
  });

  verify2FA$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loginVerify2FACode),
      map(action => action.payload),
      concatMap(twoFACode => {
        return this.loginService.verify2FACode$({ code: twoFACode.code }).pipe(
          concatMap((identity: Identity) => {
            if (identity.status === 'authenticated') {
              return [authRedirectAfterLogin({ path: '' }), authSetIdentityAndBootup(identity)];
            }

            return [];
          }),
          catchError((response: HttpErrorResponse) => {
            if (response.status === 401) {
              const { attemptsLeft: attempts = 0, banned = 0, message } = response.error;

              return of(
                loginVerify2FACodeFailure({
                  payload: {
                    attemptsLeft: Number(attempts),
                    banned: Number(banned),
                    message,
                  },
                }),
              );
            }

            return of(loginVerify2FACodeFailure({ payload: response.error }));
          }),
        );
      }),
    );
  });

  verify2FAViaRecoveryCode$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(recoveryVerifyCode),
      map(action => action.backupCode),
      concatMap(backupCode => {
        return this.loginService.verify2FAViaRecoveryCode$({ backupCode }).pipe(
          concatMap((identity: Identity) => {
            if (identity.status === 'authenticated') {
              return [authRedirectAfterLogin({ path: '' }), authSetIdentityAndBootup(identity)];
            }

            return [];
          }),
          catchError(({ error }: HttpErrorResponse) => {
            return of(loginVerify2FACodeFailure({ payload: error }));
          }),
        );
      }),
    );
  });

  constructor(
    private actions$: Actions,
    private loginService: LoginService,
    private msalService: MsalService,
    private storage: StorageService,
  ) {}
}
